import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { PartyPopper, Loader2, ArrowRight } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LogoFull } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Waitlist = () => {
  const [email, setEmail] = useState("");
  const [dogName, setDogName] = useState("");
  const [size, setSize] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/waitlist/count`).then((r) => setCount(r.data.count)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        dog_name: dogName.trim() || null,
        poodle_size: size || null,
      });
      setCount(res.data.count);
      setDone(true);
      if (res.data.status === "already_joined") {
        toast.info("You're already on the pack list!");
      } else {
        toast.success("Welcome to the CBDoodle pack!");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="bg-[#FAF7F2] px-5 py-20 sm:px-8 lg:py-28">
      <div className="mx-auto max-w-5xl">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-[#2D4338] px-6 py-14 shadow-2xl sm:px-14">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#C58F3B]/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-[#4A6B5D]/40 blur-3xl" />

          <div className="relative mx-auto max-w-xl text-center">
            <div className="mx-auto mb-6 w-fit overflow-hidden rounded-2xl">
              <LogoFull variant="dark" className="h-24 w-auto sm:h-28" />
            </div>

            <AnimatePresence mode="wait">
              {!done ? (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#E7C489]">
                    Get Early Access
                  </span>
                  <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-[#FAF7F2] sm:text-[2.75rem]">
                    Be first to help your poodle thrive
                  </h2>
                  <p className="mt-4 font-sans leading-relaxed text-[#C9D3CC]">
                    Join the CBDoodle waitlist for early access, founding-member perks, and a say in the final
                    formula. No spam — just gentle updates.
                  </p>

                  <form onSubmit={submit} data-testid="cbdoodle-waitlist-form" className="mt-8 space-y-3 text-left">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input
                        data-testid="cbdoodle-waitlist-dog-name-input"
                        type="text"
                        placeholder="Your poodle's name (optional)"
                        value={dogName}
                        onChange={(e) => setDogName(e.target.value)}
                        className="w-full rounded-full border border-white/15 bg-white/[0.06] px-5 py-3.5 font-sans text-[15px] text-white placeholder-[#9fb0a7] outline-none transition focus:border-[#C58F3B] focus:bg-white/[0.1]"
                      />
                      <Select value={size} onValueChange={setSize}>
                        <SelectTrigger
                          data-testid="cbdoodle-waitlist-size-select"
                          className="w-full rounded-full border-white/15 bg-white/[0.06] px-5 py-3.5 font-sans text-[15px] text-white data-[placeholder]:text-[#9fb0a7] focus:ring-[#C58F3B]"
                          style={{ height: "auto" }}
                        >
                          <SelectValue placeholder="Poodle size (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="toy">Toy Poodle</SelectItem>
                          <SelectItem value="miniature">Miniature Poodle</SelectItem>
                          <SelectItem value="standard">Standard Poodle</SelectItem>
                          <SelectItem value="other">Other breed / mix</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <input
                        data-testid="cbdoodle-waitlist-email-input"
                        type="email"
                        required
                        placeholder="you@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full flex-1 rounded-full border border-white/15 bg-white/[0.06] px-5 py-3.5 font-sans text-[15px] text-white placeholder-[#9fb0a7] outline-none transition focus:border-[#C58F3B] focus:bg-white/[0.1]"
                      />
                      <button
                        data-testid="cbdoodle-waitlist-submit-btn"
                        type="submit"
                        disabled={loading}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C58F3B] px-7 py-3.5 font-sans text-[15px] font-medium text-white transition-all duration-200 hover:bg-[#B07D2B] active:scale-[0.98] disabled:opacity-70"
                      >
                        {loading ? <Loader2 size={17} className="animate-spin" /> : <>Join Waitlist <ArrowRight size={16} /></>}
                      </button>
                    </div>
                  </form>

                  {count !== null && count > 0 && (
                    <p className="mt-5 font-sans text-sm text-[#9fb0a7]">
                      <span className="font-semibold text-[#E7C489]">{count}</span> poodle parent{count === 1 ? "" : "s"} already joined the pack.
                    </p>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="thanks"
                  data-testid="cbdoodle-waitlist-thankyou"
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="py-4"
                >
                  <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#C58F3B]">
                    <PartyPopper size={30} className="text-white" />
                  </div>
                  <h2 className="font-serif text-3xl font-semibold text-[#FAF7F2] sm:text-4xl">
                    Welcome to the pack! 🐾
                  </h2>
                  <p className="mx-auto mt-4 max-w-md font-sans leading-relaxed text-[#C9D3CC]">
                    {dogName ? `${dogName} and you are` : "You're"} officially on the CBDoodle waitlist. We'll be
                    in touch with early-access news and founding-member perks.
                  </p>
                  {count !== null && (
                    <p className="mt-5 font-sans text-sm text-[#9fb0a7]">
                      You're one of <span className="font-semibold text-[#E7C489]">{count}</span> poodle parents in the pack.
                    </p>
                  )}
                  <button
                    onClick={() => { setDone(false); setEmail(""); setDogName(""); setSize(""); }}
                    className="mt-7 font-sans text-sm text-[#E7C489] underline underline-offset-4 hover:text-white"
                  >
                    Add another poodle
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
